/**
 * Platform Detector Service
 * Detects whether the app is running in a web browser or native Capacitor environment
 */

let capacitorCore = null;

// Lazy load Capacitor to avoid errors in web environment
const getCapacitor = async () => {
  if (capacitorCore === null) {
    try {
      const module = await import('@capacitor/core');
      capacitorCore = module.Capacitor;
    } catch (e) {
      capacitorCore = false;
    }
  }
  return capacitorCore;
};

/**
 * Check if running in native Capacitor environment
 * @returns {boolean}
 */
export const isNative = () => {
  // Check if Capacitor is available and we're on a native platform
  if (typeof window !== 'undefined' && window.Capacitor) {
    return window.Capacitor.isNativePlatform();
  }
  return false;
};

/**
 * Check if running in web browser
 * @returns {boolean}
 */
export const isWeb = () => !isNative();

/**
 * Get current platform name
 * @returns {'ios' | 'android' | 'web'}
 */
export const getPlatform = () => {
  if (typeof window !== 'undefined' && window.Capacitor) {
    return window.Capacitor.getPlatform();
  }
  return 'web';
};

/**
 * Check if running on iOS
 * @returns {boolean}
 */
export const isIOS = () => getPlatform() === 'ios';

/**
 * Check if running on Android
 * @returns {boolean}
 */
export const isAndroid = () => getPlatform() === 'android';

/**
 * Get device info (async)
 * @returns {Promise<Object>}
 */
export const getDeviceInfo = async () => {
  if (isNative()) {
    try {
      const { Device } = await import('@capacitor/device');
      return await Device.getInfo();
    } catch (e) {
      console.warn('Device plugin not available:', e);
    }
  }
  
  // Return web-based device info
  return {
    platform: 'web',
    operatingSystem: navigator.platform,
    osVersion: navigator.userAgent,
    model: 'Browser',
    manufacturer: navigator.vendor,
    isVirtual: false,
    webViewVersion: navigator.appVersion
  };
};

const platformDetector = {
  isNative,
  isWeb,
  getPlatform,
  isIOS,
  isAndroid,
  getDeviceInfo
};

export default platformDetector;
